/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/react';
import { Helmet } from 'react-helmet';
import { CheckboxOptionsWithConfirm } from 'shared-components';
import FundingTable from '../../../atoms/funding-table';
import RemainingFundingTable from '../../../atoms/remaining-funding-table';
import OrganisationSponsors from '../../../organisms/finance/OrganisationSponsor';
import ScholarshipSponsors from '../../../organisms/finance/ScholarhipSponsor';
import BankSponsors from '../../../organisms/finance/BankSponsor';
import EducationSponsors from '../../../organisms/finance/EducationSponsor';
import OtherSponsors from '../../../organisms/finance/OtherSponsor';
import DepositFunding from '../../../organisms/finance/DepositFunding';
import QuestionPage from '../../../templates/question';
import { titleAppend } from '../../../../utilities/head';

import { useDispatch, useSelector } from 'react-redux';
import { fundingSources } from 'data';
import { ApplicationState } from '../../../../store';
import * as StudentActions from '../../../../store/student/actions';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import { IFinance, ScholarshipSponsorship } from '../../../../types';

const getFundingSources = (finance: IFinance) => {
  const {
    organisationSponsorship = {},
    scholarshipSponsorship = {},
    educationSponsorship = {},
    familySponsorship = {},
    otherSponsorship = {}
  } = finance;
  const sources = [];

  if (Object.keys(organisationSponsorship).length > 0) sources.push('organisationSponsorship');
  if (Object.keys(scholarshipSponsorship).length > 0) sources.push('scholarshipSponsorship');
  if (Object.keys(educationSponsorship).length > 0) sources.push('educationSponsorship');
  if (Object.keys(familySponsorship).length > 0) sources.push('familySponsorship');
  if (Object.keys(otherSponsorship).length > 0) sources.push('otherSponsorship');

  return sources;
};

// clone once to prevent modification of original
const fundingSourcesData = fundingSources.map(source => ({ ...source }));
const currentStep = 1;
const steps = 1;

const useData = () => {
  const financials = useSelector((state: ApplicationState) => state.student.financials);
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const config = useSelector((state: ApplicationState) => state.config);

  const sectionTitle = useSelector((state: ApplicationState) => state.config.Checklist.Finances.shortTitle);
  const complete = useSelector((state: ApplicationState) => state.student.financials.isSectionComplete);
  const { courseFeeDepositReqd, accomDepositReqd } = useSelector((state: ApplicationState) => state.student.financials);
  const missingDeposit = !courseFeeDepositReqd || !accomDepositReqd;

  const forceShowDeposit = useSelector((state: ApplicationState) => !state.config.depositUneditable && missingDeposit);
  const tuitionPostDiscount = useSelector((state: ApplicationState) => state.config.tuitionPostDiscount ?? false);

  return {
    sectionTitle,
    offer,
    config,
    forceShowDeposit,
    tuitionPostDiscount,
    complete: !!complete,
    finance: financials
  };
};

const QPage1 = () => {
  const { sectionTitle, complete, finance, offer, config, forceShowDeposit, tuitionPostDiscount } = useData();

  const [fundingSources, setFundingSources] = useState([]);
  const [isFirstUpdate, setIsFirstUpdate] = useState(true);

  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => dispatch(createNavigateForwardAction());
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onSaveDeposit = () => dispatch(StudentActions.saveDeposit());
  const onCreateSponsor = (region: string, data: any) => {
    switch (region) {
      case 'organisationSponsorship':
        dispatch(StudentActions.createOrganisationSponsor(data));
        break;
      case 'scholarshipSponsorship':
        dispatch(StudentActions.createScholarshipSponsor(data));
        break;
      case 'educationSponsorship':
        dispatch(StudentActions.createEducationSponsor(data));
        break;
      case 'familySponsorship':
        dispatch(StudentActions.createFamilySponsor(data));
        break;
      case 'otherSponsorship':
        dispatch(StudentActions.createOtherSponsor(data));
        break;
    }
  };
  const onUpdateSponsor = (region: string, key: string, data: any) => {
    switch (region) {
      case 'organisationSponsorship':
        dispatch(StudentActions.updateOrganisationSponsor(key, data));
        break;
      case 'scholarshipSponsorship':
        dispatch(StudentActions.updateScholarshipSponsor(key, data));
        break;
      case 'educationSponsorship':
        dispatch(StudentActions.updateEducationSponsor(key, data));
        break;
      case 'familySponsorship':
        dispatch(StudentActions.updateFamilySponsor(key, data));
        break;
      case 'otherSponsorship':
        dispatch(StudentActions.updateOtherSponsor(key, data));
        break;
    }
  };
  const onRemoveSponsor = (region: string, key: string) => {
    switch (region) {
      case 'organisationSponsorship':
        dispatch(StudentActions.removeOrganisationSponsor(key));
        break;
      case 'scholarshipSponsorship':
        dispatch(StudentActions.removeScholarshipSponsor(key));
        break;
      case 'educationSponsorship':
        dispatch(StudentActions.removeEducationSponsor(key));
        break;
      case 'familySponsorship':
        dispatch(StudentActions.removeFamilySponsor(key));
        break;
      case 'otherSponsorship':
        dispatch(StudentActions.removeOtherSponsor(key));
        break;
    }
  };
  const onRemoveRegion = (region: string) => {
    switch (region) {
      case 'organisationSponsorship':
        dispatch(StudentActions.removeOrganisationRegion());
        break;
      case 'scholarshipSponsorship':
        dispatch(StudentActions.removeScholarshipRegion());
        break;
      case 'educationSponsorship':
        dispatch(StudentActions.removeEducationRegion());
        break;
      case 'familySponsorship':
        dispatch(StudentActions.removeFamilyRegion());
        break;
      case 'otherSponsorship':
        dispatch(StudentActions.removeOtherRegion());
        break;
    }
  };
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const sources = getFundingSources(finance);
  useEffect(() => {
    if (fundingSources.join('') !== sources.join('') && isFirstUpdate) {
      setFundingSources(sources);
    }
    setIsFirstUpdate(false);
  }, [finance, fundingSources, isFirstUpdate, sources]);

  const getFundingSourcesCheckboxes = useCallback(() => {
    const checkboxes = [];
    const { scholarshipSponsorship = {} } = finance;

    fundingSourcesData.forEach(source => {
      checkboxes.push({
        id: source.id,
        label: source.label,
        isTicked: fundingSources.includes(source.id),
        readOnly:
          source.id === 'scholarshipSponsorship' &&
          Object.keys(scholarshipSponsorship).filter(key => {
            // filter dummy scholarship that is added when tuitionPostDiscount is true (we filter in case a rare case of uni switches tuitionPostDiscount to false later on)
            const isDummyScholarship =
              scholarshipSponsorship[key]?.scholarshipName === 'University Scholarship' &&
              scholarshipSponsorship[key]?.courseFundAmount === 0;
            return !isDummyScholarship;
          }).length > 0
      });
    });

    return checkboxes;
  }, [fundingSources]);

  const handleFundingChange = (fundingId, isTicked) => {
    if (!isTicked) {
      // remove from list
      setFundingSources(fundingSources.filter(source => source !== fundingId));
      onRemoveRegion(fundingId);
    } else {
      // add to list
      setFundingSources(fundingSources.concat([fundingId]));
      if (fundingId === 'scholarshipSponsorship' && tuitionPostDiscount) {
        /* If uni is supplying tuition fee post discount fee,
           student is not able to add scholarship sponsors normally.
           To trigger document request, add a dummy scholarship sponsor here.
         */
        const { scholarshipSponsorship = {} } = finance;
        const dummySponsor: ScholarshipSponsorship = {
          scholarshipName: 'University Scholarship',
          sponsorshipCovers: [],
          courseFundType: '',
          courseFundPercentage: 0,
          courseFundAmount: 0,
          livingFundType: '',
          livingFundPercentage: 0,
          livingFundAmountType: '',
          livingFundAmount: 0,
          courseAndLivingFundSeparate: false,
          courseAndLivingFundType: '',
          courseAndLivingFundPercentage: 0,
          courseAndLivingFundAmount: 0
        };

        if (Object.keys(scholarshipSponsorship).length === 0) {
          onCreateSponsor('scholarshipSponsorship', dummySponsor);
        }
      }
    }
  };

  const renderSponsors = fundingSources => {
    const visibleSources = fundingSourcesData.filter(fundingSourceItem =>
      fundingSources.includes(fundingSourceItem.id)
    );

    const {
      organisationSponsorship = {},
      scholarshipSponsorship = {},
      educationSponsorship = {},
      familySponsorship = {},
      otherSponsorship = {},
      fundingSummary
    } = finance;

    const filteredScholarshipSponsorship = {};
    const activeCoursesLabel = fundingSummary?.courseFees.map(course => course.label) || [];

    Object.keys(scholarshipSponsorship)
      .filter(key => {
        // filter dummy scholarship that is added when tuitionPostDiscount is true (we filter in case a rare case of uni switches tuitionPostDiscount to false later on)
        const isDummyScholarship =
          scholarshipSponsorship[key]?.scholarshipName === 'University Scholarship' &&
          scholarshipSponsorship[key]?.courseFundAmount === 0;
        // filter scholarships of removed courses
        const isActiveScholarship = scholarshipSponsorship[key]?.sponsorshipCovers.find(
          sponsorshipCourseName =>
            activeCoursesLabel?.includes(sponsorshipCourseName) || sponsorshipCourseName === 'studentLivingCost'
        );
        return !isDummyScholarship && isActiveScholarship;
      })
      .forEach(key => {
        filteredScholarshipSponsorship[key] = { ...scholarshipSponsorship[key] };
      });

    const nodes: React.ReactNode[] = visibleSources.map(fundingSourceItem => {
      switch (fundingSourceItem.id) {
        case 'organisationSponsorship':
          return (
            <OrganisationSponsors
              key={fundingSourceItem.id}
              sponsorships={organisationSponsorship}
              fundingSummary={fundingSummary}
              fundingSourceData={fundingSourceItem}
              config={config}
              onCreate={data => {
                onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        case 'scholarshipSponsorship':
          if (tuitionPostDiscount && Object.keys(filteredScholarshipSponsorship).length === 0) {
            return null;
          } else {
            return (
              <ScholarshipSponsors
                key={fundingSourceItem.id}
                sponsorships={filteredScholarshipSponsorship}
                fundingSummary={fundingSummary}
                fundingSourceData={fundingSourceItem}
                config={config}
                disabled={tuitionPostDiscount}
                onCreate={data => {
                  onCreateSponsor(fundingSourceItem.id, data);
                }}
                onUpdate={(key, data) => {
                  onUpdateSponsor(fundingSourceItem.id, key, data);
                }}
                onRemove={key => {
                  onRemoveSponsor(fundingSourceItem.id, key);
                }}
              />
            );
          }
        case 'educationSponsorship':
          return (
            <EducationSponsors
              key={fundingSourceItem.id}
              sponsorships={educationSponsorship}
              fundingSummary={fundingSummary}
              fundingSourceData={fundingSourceItem}
              config={config}
              onCreate={data => {
                onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        case 'familySponsorship':
          return (
            <BankSponsors
              key={fundingSourceItem.id}
              sponsorships={familySponsorship}
              fundingSummary={fundingSummary}
              fundingSourceData={fundingSourceItem}
              config={config}
              hasPartner={offer.hasPartner ?? false}
              onCreate={data => {
                onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        case 'otherSponsorship':
          return (
            <OtherSponsors
              key={fundingSourceItem.id}
              sponsorships={otherSponsorship}
              fundingSummary={fundingSummary}
              fundingSourceData={fundingSourceItem}
              config={config}
              onCreate={data => {
                onCreateSponsor(fundingSourceItem.id, data);
              }}
              onUpdate={(key, data) => {
                onUpdateSponsor(fundingSourceItem.id, key, data);
              }}
              onRemove={key => {
                onRemoveSponsor(fundingSourceItem.id, key);
              }}
            />
          );
        default:
          return null;
      }
    });

    return nodes;
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {sectionTitle} {titleAppend}
        </title>
      </Helmet>

      <FundingTable
        fundingSummary={finance.fundingSummary}
        inputHelperText={config.Checklist.Finances.Page1.AmountToProve.helpText}
      />

      <CheckboxOptionsWithConfirm
        id="fundingSources"
        subText="Note: you can tick more than one box"
        inputHelperText={config.Checklist.Finances.Page1.FundingSources.helpText}
        checkboxes={getFundingSourcesCheckboxes()}
        labelText="How are you planning to fund your studies and living expenses?"
        onChange={handleFundingChange}
      />

      {fundingSources.includes('scholarshipSponsorship') && tuitionPostDiscount && (
        <Alert status="info" mb={6}>
          <AlertIcon />

          <AlertDescription>
            Please note that you cannot self-declare any scholarships or discounts awarded by the university. If you
            have received a scholarship or discount that is not listed on this page, please contact the university
            directly.
          </AlertDescription>
        </Alert>
      )}

      {renderSponsors(fundingSources)}

      <DepositFunding forceShow={forceShowDeposit} fundingSummary={finance.fundingSummary} onSave={onSaveDeposit} />

      <RemainingFundingTable
        fundingSummary={finance.fundingSummary}
        inputHelperText={config.Checklist.Finances.Page1.RemainingAmountToProve.helpText}
      />
    </QuestionPage>
  );
};

export default QPage1;
